import { produce } from 'immer'
import {
  SET_EXPENSE_RECEIPT_ATTACHMENT_IDS,
  SetExpenseReceiptAttachmentIdsAction,
} from 'src/app/UpdateExpenseFlyout/redux/updateExpense.actions'
import { addExpenseRouteProps } from 'src/navigation/routeProps'
import {
  UPLOAD_ATTACHMENT_FAILURE,
  UPLOAD_ATTACHMENT_INIT,
  UPLOAD_ATTACHMENT_SUCCESS,
  UploadAttachmentActions,
} from 'src/queries/receiptAttachments/receiptAttachments.events'
import {
  POST_CREATE_EXPENSE_FAILURE,
  POST_CREATE_EXPENSE_INIT,
  POST_CREATE_EXPENSE_SUCCESS,
  PostCreateExpenseActions,
} from 'src/redux/actions/data/expenses.actions'
import { createFlyoutReducer } from 'src/redux/genericReducers'
import { ErrorDetailMap, parseBackendErrors } from 'src/utils/error.utils'

// NOTE (EX-39746): the report-detail READ moved to React Query, so the
// GET_EXPENSE_REPORT_* cases and the fetchSuccess/fetchFailure flags are gone;
// AddExpenseFlyout reads the report via useQuery and gates not-found on the
// query's isError. The create-expense + upload state below stays redux.
export interface AddExpenseState {
  receiptAttachmentIds: string[]
  uploadingAttachmentThumbnails: Record<string, string>
  postingExpense: boolean
  errorDetailMap?: ErrorDetailMap<string>
  topLevelError?: string
}

export const initialState: AddExpenseState = {
  receiptAttachmentIds: [],
  uploadingAttachmentThumbnails: {},
  postingExpense: false,
}

export type Actions =
  | UploadAttachmentActions
  | SetExpenseReceiptAttachmentIdsAction
  | PostCreateExpenseActions

const CoreAddExpenseReducer = (
  state = initialState,
  action: Actions,
): AddExpenseState => {
  return produce(state, (draft) => {
    switch (action.type) {
      case POST_CREATE_EXPENSE_INIT: {
        draft.postingExpense = true
        draft.errorDetailMap = undefined
        draft.topLevelError = undefined
        return draft
      }

      case POST_CREATE_EXPENSE_SUCCESS: {
        draft.postingExpense = false
        return draft
      }

      case POST_CREATE_EXPENSE_FAILURE: {
        draft.postingExpense = false
        const errors = parseBackendErrors(action.payload)
        draft.errorDetailMap = errors.errorDetailMap
        draft.topLevelError = errors.topLevelError
        return draft
      }

      case SET_EXPENSE_RECEIPT_ATTACHMENT_IDS: {
        draft.receiptAttachmentIds = action.payload.ids
        return draft
      }

      case UPLOAD_ATTACHMENT_INIT: {
        draft.uploadingAttachmentThumbnails = {
          ...draft.uploadingAttachmentThumbnails,
          [action.payload.uploadId]: action.payload.thumbnailUrl,
        }
        return draft
      }

      case UPLOAD_ATTACHMENT_FAILURE: {
        draft.uploadingAttachmentThumbnails = Object.keys(
          draft.uploadingAttachmentThumbnails,
        )
          .filter((id) => id !== action.payload.uploadId)
          .reduce(
            (acc, curr) => ({
              ...acc,
              [curr]: draft.uploadingAttachmentThumbnails[curr],
            }),
            {},
          )
        return draft
      }

      case UPLOAD_ATTACHMENT_SUCCESS: {
        draft.uploadingAttachmentThumbnails = Object.keys(
          draft.uploadingAttachmentThumbnails,
        )
          .filter((id) => id !== action.payload.uploadId)
          .reduce(
            (acc, curr) => ({
              ...acc,
              [curr]: draft.uploadingAttachmentThumbnails[curr],
            }),
            {},
          )
        draft.receiptAttachmentIds = [
          ...draft.receiptAttachmentIds,
          action.payload.id,
        ]
        return draft
      }
    }
  })
}

const AddExpenseReducer = createFlyoutReducer(
  addExpenseRouteProps,
  initialState,
  CoreAddExpenseReducer,
)

export default AddExpenseReducer
